import type { IWebFunnelsConfigFile } from 'root-constants'
import { CurrentEnvironment } from 'root-constants'
import { getFunnelsConfig } from 'helpers/getWebFunnelsConfig'
import type { IGeoDataResponse } from 'models/geo.model'
import { useZustandStore } from 'zustandGlobalStore'

export type TComplianceVersion = 'ca' | 'nyc' | 'us' | null

interface IResolveComplianceVersionProps {
  configFile: IWebFunnelsConfigFile
  geoData: IGeoDataResponse | null
  leadCountry?: string | null
}

const NEW_YORK_CITY = 'new york'

const getForcedComplianceVersion = (): TComplianceVersion => {
  if (import.meta.env.CURRENT_ENV === CurrentEnvironment.PROD) return null

  const forcedVersion = new URLSearchParams(window.location.search).get(
    'force_cmpl',
  )

  return forcedVersion === 'ca' ||
    forcedVersion === 'nyc' ||
    forcedVersion === 'us'
    ? forcedVersion
    : null
}

export const resolveComplianceVersion = ({
  configFile,
  geoData,
  leadCountry,
}: IResolveComplianceVersionProps): TComplianceVersion => {
  const forcedVersion = getForcedComplianceVersion()
  if (forcedVersion) return forcedVersion

  const country = (geoData?.country || leadCountry || '').toUpperCase()
  if (country !== 'US') return null

  const isCalifornia = (geoData?.regionCode || '').toUpperCase() === 'CA'

  if (isCalifornia) return configFile.caCmpl === false ? null : 'ca'

  const isNewYork = (geoData?.city || '')
    .toLowerCase()
    .startsWith(NEW_YORK_CITY)

  if (isNewYork) return configFile.nycCmpl === false ? null : 'nyc'

  return configFile.usCmpl === false ? null : 'us'
}

export const getComplianceVersion = (): TComplianceVersion => {
  const state = useZustandStore.getState()

  return resolveComplianceVersion({
    configFile: getFunnelsConfig(),
    geoData: state.geoData,
    leadCountry: state.lead?.country,
  })
}

const STRICT_COMPLIANCE_VERSIONS: TComplianceVersion[] = ['ca', 'nyc']

export const getIsStrictCompliance = (version: TComplianceVersion): boolean =>
  STRICT_COMPLIANCE_VERSIONS.includes(version)
